"use client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  months,
  monthsEn,
  toNepaliNumber,
  useNepaliDatepicker,
} from "../hooks/useNepaliDatepicker";
import { type CalendarDay } from "../lib/api";
import {
  convertADToDatePair,
  normalizeDateString,
  normalizeHolidayWeekdays,
  WEEKDAYS_AD,
  WEEKDAYS_BS,
} from "../lib/calendarUtils";
import { getCurrentBSDate } from "../lib/dateConverter";
import "./NepaliCalendar.css";

export interface NepaliDatePickerProps {
  year?: number;
  month?: number;
  type?: "icon" | "input";
  placeholder?: string;
  holidayWeekdays?: string | string[];
  showToggle?: boolean;
  calendarMode?: "bs" | "ad";
  showNationalHoliday?: boolean;
  className?: string;
  calendarWrapperClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  navButtonClassName?: string;
  weekdaysClassName?: string;
  weekdayClassName?: string;
  calendarDaysClassName?: string;
  calendarDayClassName?: string;
  bsDateClassName?: string;
  adDateClassName?: string;
  toggleWrapperClassName?: string;
  toggleButtonClassName?: string;
  styles?: any;
  prevArrow?: string | React.ReactNode;
  nextArrow?: string | React.ReactNode;
  weekdayLabels?: string[];
  value?: string;
  minDate?: string;
  maxDate?: string;
  disabled?: boolean;
  showArrows?: boolean;
  tillCurrentYear?: boolean;
  onDateSelect?: (date: CalendarDay) => void;
  onMonthChange?: (year: number, month: number) => void;
  onCalendarModeChange?: (mode: "bs" | "ad") => void;
}

const POPUP_GAP = 8;
const VIEWPORT_MARGIN = 16;

const NepaliDatePicker: React.FC<NepaliDatePickerProps> = ({
  year: initialYear,
  month: initialMonth,
  type = "input",
  placeholder = "Select date",
  holidayWeekdays = "Saturday",
  showToggle = false,
  calendarMode: initialCalendarMode = "bs",
  showNationalHoliday = false,
  className = "",
  calendarWrapperClassName = "",
  headerClassName = "",
  titleClassName = "",
  navButtonClassName = "",
  weekdaysClassName = "",
  weekdayClassName = "",
  calendarDaysClassName = "",
  calendarDayClassName = "",
  bsDateClassName = "",
  adDateClassName = "",
  toggleWrapperClassName = "",
  toggleButtonClassName = "",
  styles = {},
  prevArrow = "←",
  nextArrow = "→",
  weekdayLabels,
  value,
  minDate: rawMinDate,
  maxDate: rawMaxDate,
  disabled = false,
  showArrows = true,
  tillCurrentYear = false,
  onDateSelect,
  onMonthChange,
  onCalendarModeChange,
}) => {
  // Normalize minDate and maxDate to YYYY-MM-DD format
  const minDate = normalizeDateString(rawMinDate);
  const maxDate = normalizeDateString(rawMaxDate);

  const {
    currentBSYear,
    currentBSMonth,
    isLoading,
    isYearLoading,
    isMonthLoading,
    isInputLoading,
    calendarMode,
    isIndexedDBInitialized,
    showMonthDropdown,
    showYearDropdown,
    selectedBSKey,
    selectedADKey,
    primaryADMonth,
    primaryADYear,
    titleRef,
    setShowMonthDropdown,
    setShowYearDropdown,
    getDataForKey,
    getADMonthRange,
    getDisplayMonthYear,
    getDropdownDisplayValues,
    handleDayClick,
    handlePrevMonth,
    handleNextMonth,
    handleCalendarModeChange,
    handleMonthSelect,
    handleYearSelect,
    handleInputDate,
    fetchMonthData,
    fetchAllBSMonthsForADMonth,
    navigateToValuePropMonth,
  } = useNepaliDatepicker({
    year: initialYear,
    month: initialMonth,
    calendarMode: initialCalendarMode,
    value,
    onDateSelect,
    onMonthChange,
    onCalendarModeChange,
  });

  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [adModeDays, setAdModeDays] = useState<CalendarDay[] | null>(null);
  const [popupPosition, setPopupPosition] = useState<{
    top: number;
    left: number;
    width: number;
    positionAbove: boolean;
  } | null>(null);

  const popupRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userTypedInputRef = useRef<string | null>(null);

  const holidayIndexSet = useMemo(
    () => normalizeHolidayWeekdays(holidayWeekdays),
    [holidayWeekdays]
  );

  // Sync input value with value prop
  useEffect(() => {
    if (
      userTypedInputRef.current !== null &&
      inputValue === userTypedInputRef.current
    ) {
      setTimeout(() => {
        userTypedInputRef.current = null;
      }, 100);
      return;
    }

    if (value !== undefined && (value === null || value === "")) {
      if (inputValue !== "") setInputValue("");
      return;
    }

    if (value) {
      const displayValue =
        calendarMode === "bs" ? selectedBSKey : selectedADKey;
      if (displayValue && displayValue !== inputValue) {
        setInputValue(displayValue);
      } else if (!displayValue) {
        const datePair = convertADToDatePair(value);
        if (datePair) {
          const newValue =
            calendarMode === "bs" ? datePair.bsString : datePair.adString;
          if (newValue !== inputValue) setInputValue(newValue);
        }
      }
    }
  }, [calendarMode, selectedBSKey, selectedADKey, value]);

  // Fetch AD mode days
  useEffect(() => {
    if (
      calendarMode === "ad" &&
      isIndexedDBInitialized &&
      fetchAllBSMonthsForADMonth
    ) {
      const adYear = primaryADYear ?? getDisplayMonthYear().yearNum;
      const adMonth = primaryADMonth ?? getDisplayMonthYear().monthNum;

      if (adYear >= 1921 && adYear <= 2032 && adMonth >= 1 && adMonth <= 12) {
        fetchAllBSMonthsForADMonth(adYear, adMonth).then(setAdModeDays);
      } else {
        setAdModeDays(null);
      }
    } else {
      setAdModeDays(null);
    }
  }, [
    calendarMode,
    primaryADMonth,
    primaryADYear,
    isIndexedDBInitialized,
    fetchAllBSMonthsForADMonth,
  ]);

  const calculatePopupPosition = useCallback(() => {
    if (!wrapperRef.current || !popupRef.current) return;

    const inputRect = wrapperRef.current.getBoundingClientRect();
    const popupHeight = popupRef.current.offsetHeight || 400;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - inputRect.bottom - POPUP_GAP;
    const spaceAbove = inputRect.top - POPUP_GAP;
    const positionAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;

    let top = positionAbove
      ? inputRect.top - popupHeight - POPUP_GAP
      : inputRect.bottom + POPUP_GAP;
    top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(top, viewportHeight - popupHeight - VIEWPORT_MARGIN)
    );

    const preferredWidth = 360;
    const popupWidth = Math.max(
      320,
      Math.min(preferredWidth, viewportWidth - VIEWPORT_MARGIN * 2)
    );

    let left = inputRect.left;
    if (left + popupWidth > viewportWidth - VIEWPORT_MARGIN)
      left = viewportWidth - popupWidth - VIEWPORT_MARGIN;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

    setPopupPosition({ top, left, width: popupWidth, positionAbove });
  }, []);

  useEffect(() => {
    if (showCalendarPopup) {
      const timer = setTimeout(calculatePopupPosition, 0);
      const handleUpdate = () => requestAnimationFrame(calculatePopupPosition);
      window.addEventListener("scroll", handleUpdate, true);
      window.addEventListener("resize", handleUpdate);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("scroll", handleUpdate, true);
        window.removeEventListener("resize", handleUpdate);
      };
    } else {
      setPopupPosition(null);
    }
  }, [showCalendarPopup, calculatePopupPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (titleRef.current && !titleRef.current.contains(target)) {
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
      }
      if (
        popupRef.current &&
        !popupRef.current.contains(target) &&
        wrapperRef.current &&
        !wrapperRef.current.contains(target)
      ) {
        setShowCalendarPopup(false);
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [titleRef, setShowMonthDropdown, setShowYearDropdown]);

  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, []);

  const isDateInRange = useCallback(
    (adDateString: string | undefined): boolean => {
      if (!adDateString) return false;
      if (!minDate && !maxDate) return true;
      if (minDate && adDateString < minDate) return false;
      if (maxDate && adDateString > maxDate) return false;
      return true;
    },
    [minDate, maxDate]
  );

  const formatInputValue = useCallback((val: string): string => {
    const digits = val.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 4) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
  }, []);

  const showRangeError = useCallback(() => {
    setErrorMessage("Date out of allowed range");
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    errorTimeoutRef.current = setTimeout(() => {
      setErrorMessage("");
      errorTimeoutRef.current = null;
    }, 4000);
  }, []);

  const handleDatePickerDayClick = useCallback(
    (day: CalendarDay) => {
      if (!isDateInRange(day.ad.date)) return;
      handleDayClick(day);
      setShowMonthDropdown(false);
      setShowYearDropdown(false);
      setInputValue(calendarMode === "bs" ? day.bs.date : day.ad.date);
    },
    [
      handleDayClick,
      calendarMode,
      setShowMonthDropdown,
      setShowYearDropdown,
      isDateInRange,
    ]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(formatInputValue(e.target.value));
    },
    [formatInputValue]
  );

  const handleInputBlur = useCallback(async () => {
    if (!inputValue || inputValue.length < 10) {
      if (value === undefined && selectedBSKey) {
        setInputValue(calendarMode === "bs" ? selectedBSKey : selectedADKey);
      }
      return;
    }

    userTypedInputRef.current = inputValue;
    const result = await handleInputDate(inputValue, calendarMode);

    if (!result) {
      showRangeError();
      userTypedInputRef.current = null;
      if (selectedBSKey) {
        setInputValue(calendarMode === "bs" ? selectedBSKey : selectedADKey);
      } else {
        setInputValue("");
      }
    }
  }, [
    value,
    inputValue,
    selectedBSKey,
    selectedADKey,
    calendarMode,
    handleInputDate,
    showRangeError,
  ]);

  const handleInputKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        inputRef.current?.blur();
        await handleInputBlur();
      } else if (e.key === "Escape") setShowCalendarPopup(false);
      else if (e.key === "Tab") {
        setShowCalendarPopup(false);
        inputRef.current?.blur();
      }
    },
    [handleInputBlur]
  );

  const handleInputFocus = useCallback(
    async (e: React.FocusEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (value && isIndexedDBInitialized) await navigateToValuePropMonth();
      setShowCalendarPopup(true);
    },
    [value, isIndexedDBInitialized, navigateToValuePropMonth]
  );

  const renderCalendarDays = useCallback(() => {
    if (
      isLoading ||
      isYearLoading ||
      !isIndexedDBInitialized ||
      isMonthLoading ||
      isInputLoading
    ) {
      return (
        <div
          className="nepali-datepicker-loading-spinner-container"
          style={styles.loadingSpinner}
        >
          <div className="nepali-datepicker-loading-spinner" />
          <div
            className="nepali-datepicker-loading-text"
            style={styles.loadingText}
          >
            Loading...
          </div>
        </div>
      );
    }

    const currentData = getDataForKey(currentBSYear, currentBSMonth);
    if (!currentData) return null;

    let { days, otherData } = currentData;
    let startingWeekday = parseInt(otherData.starting_weekday.number);

    const currentBS = getCurrentBSDate();
    if (currentBS && calendarMode === "bs") {
      const todayBSDate = `${currentBS.bs_y}-${String(currentBS.bs_m).padStart(
        2,
        "0"
      )}-${String(currentBS.bs_d).padStart(2, "0")}`;
      if (
        currentBS.bs_y === currentBSYear &&
        currentBS.bs_m === currentBSMonth
      ) {
        days = days.map((day) => ({
          ...day,
          today: day.bs.date === todayBSDate,
        }));
      }
    }

    if (calendarMode === "ad" && adModeDays?.length) {
      days = adModeDays;
      if (currentBS) {
        const todayBSDate = `${currentBS.bs_y}-${String(
          currentBS.bs_m
        ).padStart(2, "0")}-${String(currentBS.bs_d).padStart(2, "0")}`;
        days = days.map((day) => ({
          ...day,
          today: day.bs.date === todayBSDate,
        }));
      }
      const displayInfo = getDisplayMonthYear();
      startingWeekday = new Date(
        displayInfo.yearNum,
        displayInfo.monthNum - 1,
        1
      ).getDay();
    }

    const daysArray: React.ReactNode[] = [];
    for (let i = 0; i < startingWeekday; i++) {
      daysArray.push(
        <div key={`empty-${i}`} className="nepali-datepicker-day empty" />
      );
    }

    days.forEach((day, index) => {
      const weekdayIndex = (startingWeekday + index) % 7;
      const isHolidayWeekday = holidayIndexSet.has(weekdayIndex);
      const isNationalHoliday =
        showNationalHoliday &&
        day.events?.some((e) => e.national_holiday === 1);
      const isHoliday = isHolidayWeekday || isNationalHoliday;
      const isSelected =
        calendarMode === "bs"
          ? selectedBSKey === day.bs.date
          : selectedADKey === day.ad.date;
      const isDisabled = !day.ad?.date || !isDateInRange(day.ad.date);

      daysArray.push(
        <div
          key={`day-${index}`}
          className={`nepali-datepicker-day ${calendarDayClassName} ${
            isHoliday ? "holiday" : ""
          } ${day.today ? "today" : ""} ${isSelected ? "selected" : ""} ${
            isDisabled ? "disabled" : ""
          }`}
          onClick={() => !isDisabled && handleDatePickerDayClick(day)}
          style={{
            ...styles.calendarDay,
            ...(isHoliday ? styles.holiday : {}),
            ...(isDisabled
              ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "none" }
              : {}),
          }}
        >
          {calendarMode === "bs" ? (
            <>
              <div
                className={`bs-date ${bsDateClassName}`}
                style={{
                  ...styles.bsDate,
                  color:
                    isSelected || day.today
                      ? undefined
                      : isNationalHoliday
                      ? "#ef4444"
                      : styles.bsDate?.color,
                }}
              >
                {day.bs?.date_np || ""}
              </div>
              <div
                className={`ad-date ad-corner ${adDateClassName}`}
                style={{
                  position: "absolute",
                  right: "4px",
                  bottom: "4px",
                  fontSize: "8px",
                  color: isNationalHoliday ? "#ef4444" : "#111827",
                  opacity: 0.6,
                  fontWeight: 400,
                }}
              >
                {day.ad?.ad_d ?? ""}
              </div>
            </>
          ) : (
            <div
              className={`ad-date ${adDateClassName}`}
              style={{
                ...styles.adDate,
                color:
                  isSelected || day.today
                    ? undefined
                    : isNationalHoliday
                    ? "#ef4444"
                    : styles.adDate?.color,
              }}
            >
              {day.ad?.ad_d ?? ""}
            </div>
          )}
        </div>
      );
    });

    const remaining = 42 - daysArray.length;
    for (let i = 0; i < remaining; i++) {
      daysArray.push(
        <div key={`empty-end-${i}`} className="nepali-datepicker-day empty" />
      );
    }

    return daysArray;
  }, [
    isLoading,
    isYearLoading,
    isIndexedDBInitialized,
    isMonthLoading,
    isInputLoading,
    getDataForKey,
    currentBSYear,
    currentBSMonth,
    calendarMode,
    adModeDays,
    holidayIndexSet,
    showNationalHoliday,
    selectedBSKey,
    selectedADKey,
    calendarDayClassName,
    handleDatePickerDayClick,
    styles,
    bsDateClassName,
    adDateClassName,
    getDisplayMonthYear,
    isDateInRange,
  ]);

  const renderMonths = useCallback(() => {
    const monthList = calendarMode === "bs" ? months : monthsEn;
    const { selectedMonth } = getDropdownDisplayValues();
    return monthList.map((month, index) => (
      <div
        key={index}
        className="nepali-datepicker-dropdown-item"
        onClick={() => handleMonthSelect(index + 1)}
        style={{
          padding: "8px",
          cursor: "pointer",
          backgroundColor: selectedMonth === index + 1 ? "#e8f4f8" : "white",
        }}
      >
        {month}
      </div>
    ));
  }, [calendarMode, getDropdownDisplayValues, handleMonthSelect]);

  const renderYears = useCallback(() => {
    const { selectedYear } = getDropdownDisplayValues();
    const years: number[] = [];
    const currentBSYearNow = getCurrentBSDate()?.bs_y || 2081;
    const currentADYearNow = new Date().getFullYear();

    if (calendarMode === "bs") {
      const endYear = tillCurrentYear ? currentBSYearNow : 2089;
      for (let i = 2033; i <= endYear; i++) years.push(i);
    } else {
      const endYear = tillCurrentYear ? currentADYearNow : 2033;
      for (let i = 1943; i <= endYear; i++) years.push(i);
    }

    return years.map((y) => (
      <div
        key={y}
        className="nepali-datepicker-dropdown-item"
        onClick={() => handleYearSelect(y)}
        style={{
          padding: "8px",
          cursor: "pointer",
          backgroundColor: selectedYear === y ? "#e8f4f8" : "white",
        }}
      >
        {calendarMode === "bs" ? toNepaliNumber(y) : y}
      </div>
    ));
  }, [
    calendarMode,
    getDropdownDisplayValues,
    handleYearSelect,
    tillCurrentYear,
  ]);

  const weekdaysToDisplay =
    weekdayLabels || (calendarMode === "bs" ? WEEKDAYS_BS : WEEKDAYS_AD);

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <div
        className="nepali-datepicker-input"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px 12px",
          border: type === "input" ? "1px solid #ccc" : "none",
          borderRadius: type === "input" ? "4px" : "0",
          cursor: type === "input" ? "text" : "pointer",
          justifyContent: type === "input" ? "space-between" : "flex-start",
          width: type === "input" ? "200px" : "fit-content",
          ...styles.datepickerInput,
        }}
        onClick={() => {
          if (type === "icon") setShowCalendarPopup(!showCalendarPopup);
          else inputRef.current?.focus();
        }}
      >
        {type === "input" && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputFocus}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled || isInputLoading}
            placeholder={placeholder}
            style={{
              border: "none",
              outline: "none",
              flex: 1,
              minWidth: 0,
              background: "transparent",
              color: inputValue ? undefined : "#999",
              cursor: isInputLoading ? "wait" : "text",
              ...styles.datepickerInputField,
            }}
          />
        )}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={type === "icon" ? "20" : "16"}
          height={type === "icon" ? "20" : "16"}
          viewBox="0 0 24 24"
          fill="none"
          stroke="black"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            ...styles.datepickerIcon,
            ...(type === "input" ? { marginLeft: "auto", flexShrink: 0 } : {}),
          }}
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </div>

      {errorMessage && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: "4px",
            padding: "8px 12px",
            backgroundColor: "#fee2e2",
            color: "#991b1b",
            borderRadius: "4px",
            fontSize: "12px",
            border: "1px solid #fecaca",
            zIndex: 1001,
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          {errorMessage}
        </div>
      )}

      {showCalendarPopup &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`nepali-datepicker ${className} ${calendarWrapperClassName}`}
            style={{
              ...styles.calendarWrapper,
              position: "fixed",
              top: popupPosition ? `${popupPosition.top}px` : "0px",
              left: popupPosition ? `${popupPosition.left}px` : "0px",
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              zIndex: 9999,
              width: "fit-content",
              minWidth: "360px",
            }}
            ref={popupRef}
          >
            {showToggle && (
              <div
                className={`nepali-datepicker-toggle-wrapper ${toggleWrapperClassName}`}
                style={styles.toggleWrapper}
              >
                <div
                  className={`nepali-datepicker-toggle-switch ${toggleButtonClassName}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCalendarModeChange(
                      calendarMode === "bs" ? "ad" : "bs"
                    );
                  }}
                  style={{
                    position: "relative",
                    width: "50px",
                    height: "24px",
                    borderRadius: "12px",
                    backgroundColor:
                      calendarMode === "bs" ? "#4169e1" : "#e5e7eb",
                    cursor: "pointer",
                    transition: "background-color 0.3s",
                    ...styles.toggleSwitch,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "2px",
                      left: calendarMode === "bs" ? "2px" : "26px",
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "white",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      transition: "left 0.3s",
                      ...styles.toggleSlider,
                    }}
                  />
                </div>
                <span style={{ marginLeft: "8px", fontSize: "14px" }}>
                  {calendarMode === "bs" ? "BS" : "AD"}
                </span>
              </div>
            )}

            <div
              className={`nepali-datepicker-header ${headerClassName}`}
              style={{
                ...styles.header,
                justifyContent: showArrows ? "space-between" : "center",
              }}
            >
              {showArrows && (
                <button
                  className={`nepali-datepicker-nav-button ${navButtonClassName}`}
                  onClick={handlePrevMonth}
                  disabled={
                    isLoading ||
                    isYearLoading ||
                    isMonthLoading ||
                    isInputLoading
                  }
                  style={{
                    ...styles.navButton,
                    ...(isLoading ||
                    isYearLoading ||
                    isMonthLoading ||
                    isInputLoading
                      ? styles.navButtonDisabled
                      : {}),
                  }}
                >
                  {prevArrow}
                </button>
              )}

              <div
                ref={titleRef}
                className={`nepali-datepicker-title ${titleClassName}`}
                style={{
                  ...styles.title,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "2px",
                  cursor: "pointer",
                  position: "relative",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    onClick={() => {
                      setShowMonthDropdown(!showMonthDropdown);
                      setShowYearDropdown(false);
                    }}
                    style={{
                      backgroundColor: showMonthDropdown
                        ? "#e8f4f8"
                        : "transparent",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    {getDisplayMonthYear().month}
                  </div>
                  <span>/</span>
                  <div
                    onClick={() => {
                      setShowYearDropdown(!showYearDropdown);
                      setShowMonthDropdown(false);
                    }}
                    style={{
                      backgroundColor: showYearDropdown
                        ? "#e8f4f8"
                        : "transparent",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    {getDisplayMonthYear().year}
                  </div>
                </div>

                {calendarMode === "bs" && getADMonthRange() && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#6b7280",
                      marginLeft: "4px",
                    }}
                  >
                    {getADMonthRange()}
                  </div>
                )}

                {showMonthDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      backgroundColor: "white",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 1000000,
                      width: "150px",
                      marginTop: "4px",
                    }}
                  >
                    {renderMonths()}
                  </div>
                )}
                {showYearDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      backgroundColor: "white",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 1000000,
                      width: "100px",
                      marginTop: "4px",
                    }}
                  >
                    {renderYears()}
                  </div>
                )}
              </div>

              {showArrows && (
                <button
                  className={`nepali-datepicker-nav-button ${navButtonClassName}`}
                  onClick={handleNextMonth}
                  disabled={
                    isLoading ||
                    isYearLoading ||
                    isMonthLoading ||
                    isInputLoading
                  }
                  style={{
                    ...styles.navButton,
                    ...(isLoading ||
                    isYearLoading ||
                    isMonthLoading ||
                    isInputLoading
                      ? styles.navButtonDisabled
                      : {}),
                  }}
                >
                  {nextArrow}
                </button>
              )}
            </div>

            <div
              className={`nepali-datepicker-weekdays ${weekdaysClassName}`}
              style={styles.weekdays}
            >
              {weekdaysToDisplay.map((day, index) => (
                <div
                  key={index}
                  className={`nepali-datepicker-weekday ${weekdayClassName} ${
                    holidayIndexSet.has(index) ? "holiday" : ""
                  }`}
                  style={{
                    ...styles.weekday,
                    ...(holidayIndexSet.has(index)
                      ? styles.weekdayHoliday
                      : {}),
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            <div
              className={`nepali-datepicker-days ${calendarDaysClassName}`}
              style={styles.calendarDays}
            >
              {renderCalendarDays()}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default NepaliDatePicker;
``;
